'use strict';

/**
 * Pict-Provider-OrgAPI
 * --------------------
 * Browser-side client for organization settings (/1.0/Org/Settings). Backs the
 * Org Settings section of the settings hub. Gated server-side by platform.manage.
 */

const libPictProvider = require('pict').ServiceProviderBase;
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'OrgAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/'
};

class PictProviderOrgAPI extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		let tmpOptions = Object.assign({}, _DefaultProviderConfiguration, pOptions || {});
		super(pFable, tmpOptions, pServiceHash);
		this.routePrefix = this.options.RoutePrefix;
	}

	initializeAppData()
	{
		if (!this.fable.AppData.Org) { this.fable.AppData.Org = { Settings: null }; }
	}

	_url(pPath)
	{
		let tmpPrefix = this.routePrefix;
		if (tmpPrefix.charAt(tmpPrefix.length - 1) !== '/') { tmpPrefix += '/'; }
		return tmpPrefix + pPath.replace(/^\//, '');
	}

	getSettings()
	{
		this.initializeAppData();
		return libFrameworkClient.getJSON(this.fable, this._url('Org/Settings'))
			.then((pBody) =>
			{
				this.fable.AppData.Org.Settings = (pBody && pBody.Org) ? pBody.Org : null;
				return this.fable.AppData.Org.Settings;
			})
			.catch(() =>
			{
				this.fable.AppData.Org.Settings = null;
				return this.fable.AppData.Org.Settings;
			});
	}

	updateSettings(pPatch)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/Settings'), pPatch || {})
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				this.fable.AppData.Org.Settings = pBody.Org || this.fable.AppData.Org.Settings;
				return pBody.Org;
			});
	}

	// Live availability check for a plan sheet Code, backing the Org Settings field. Returns
	// { Code, Available, Reason }. Excludes this plan sheet's own code, so keeping it reads free.
	checkCode(pCode)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Org/CodeAvailable/' + encodeURIComponent(String(pCode || '').trim())));
	}

	getDraftingKit()
	{
		this.initializeAppData();
		return libFrameworkClient.getJSON(this.fable, this._url('Org/DraftingKit'))
			.then((pBody) =>
			{
				let tmpKit = pBody ? pBody : null;
				this.fable.AppData.Org.DraftingKit = tmpKit;
				return tmpKit;
			})
			.catch(() =>
			{
				this.fable.AppData.Org.DraftingKit = null;
				return null;
			});
	}

	setDraftingKit(pIDDefaultVisionType)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/DraftingKit'), { IDDefaultVisionType: pIDDefaultVisionType || 0 })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				if (pBody.Lexicon && this.fable.AppData.Auth && this.fable.AppData.Auth.User) { this.fable.AppData.Auth.User.Lexicon = pBody.Lexicon; }
				return pBody;
			});
	}

	saveDraftingKitTerms(pTerms)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/DraftingKit/Terms'), { Terms: pTerms || {} })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				if (pBody.Resolved && this.fable.AppData.Auth && this.fable.AppData.Auth.User) { this.fable.AppData.Auth.User.Lexicon = pBody.Resolved; }
				return pBody;
			});
	}

	// Save the default kit's default connection (link) style; returns { Success, ConnectionStyle (sanitized) }.
	saveDraftingKitConnectionStyle(pStyle)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/DraftingKit/ConnectionStyle'), { ConnectionStyle: pStyle || {} })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				return pBody;
			});
	}

	// Save the default kit's chosen sticker set (a known set key, or '' to clear). Returns
	// { Success, DefaultStickerSet, StickerSet }; updates the live session's house StickerSet so a
	// whiteboard opened after this reflects it without a reload.
	saveDraftingKitDefaultStickerSet(pKey)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/DraftingKit/DefaultStickerSet'), { DefaultStickerSet: pKey || '' })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				if (this.fable.AppData.Auth && this.fable.AppData.Auth.User) { this.fable.AppData.Auth.User.StickerSet = pBody.StickerSet || ''; }
				return pBody;
			});
	}

	// The org brand set profile moodboards open on. Stored on the house-kit manifest (BrandStickerSet); the
	// server refreshes the session's ProfileStickerSet so a profile board opened later this session picks it up.
	saveDraftingKitBrandStickerSet(pKey)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/DraftingKit/BrandStickerSet'), { BrandStickerSet: pKey || '' })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				if (this.fable.AppData.Auth && this.fable.AppData.Auth.User) { this.fable.AppData.Auth.User.ProfileStickerSet = pBody.ProfileStickerSet || ''; }
				return pBody;
			});
	}

	// The plan sheet's top-bar backdrop (WI #360): the baked moodboard crop the shell paints behind the
	// brand / nav / account chrome, so each plan sheet's bar reads as distinct. getTopBar() fetches just
	// the SVG, which is all the load path needs; pass true to also get TopBarBoardJSON, the re-editable
	// source board the composer reopens. A failed fetch resolves to no backdrop rather than rejecting --
	// a missing decoration must never keep the app from painting.
	getTopBar(pIncludeBoard)
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Plansheet/TopBar' + (pIncludeBoard ? '?IncludeBoard=1' : '')))
			.then((pBody) => pBody || { TopBarBackdropSVG: '' })
			.catch(() => ({ TopBarBackdropSVG: '' }));
	}

	// Store a baked crop and the board it came from. An empty pSVG clears the backdrop (and the server
	// drops the stored board with it, so the two never drift apart).
	saveTopBar(pSVG, pBoard)
	{
		let tmpPayload = { TopBarBackdropSVG: String(pSVG || '') };
		if (pBoard) { tmpPayload.TopBarBoardJSON = pBoard; }
		return libFrameworkClient.putJSON(this.fable, this._url('Plansheet/TopBar'), tmpPayload)
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				return pBody;
			});
	}

	// The plan sheet's hidden sidebar entries. getNav returns { HiddenNavItems, Catalog } for the owner
	// picker; saveNav writes the list and updates the live session so the sidebar re-renders without a reload.
	getNav()
	{
		return libFrameworkClient.getJSON(this.fable, this._url('Org/Nav'));
	}

	saveNav(pHiddenNavItems)
	{
		return libFrameworkClient.putJSON(this.fable, this._url('Org/Nav'), { HiddenNavItems: Array.isArray(pHiddenNavItems) ? pHiddenNavItems : [] })
			.then((pBody) =>
			{
				if (!pBody || !pBody.Success) { throw new Error((pBody && pBody.Error) || 'HTTP 200'); }
				if (this.fable.AppData.Auth && this.fable.AppData.Auth.User) { this.fable.AppData.Auth.User.HiddenNavItems = pBody.HiddenNavItems || []; }
				return pBody;
			});
	}
}

module.exports = PictProviderOrgAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
